import { useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { usePrefersReducedMotion } from '../lib/motion';
import { useScrollMemory } from '../lib/use-scroll-memory';
import { invalidateExpedienteTimeline, useExpediente, useMe } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { CADENCIA, RIESGO_LABEL, type EstadoExpediente, type Persona } from '../lib/phase1';
import { Button, Card, Dialog, Pill, type Tone } from '../components/ui';
import { PersonaIcon, valIcon } from '../components/icons';
import { DocUploadFlow } from '../components/doc-upload';
import { BigButton, ProgressBar, friendlyEstado } from '../components/flow';
import {
  InfoNote,
  ReasonModal,
  RiskGauge,
  TabStrip,
  UboTree,
  riesgoTone,
  valTone,
} from '../components/compliance';
import { ExpedienteEditDialog } from '../components/ExpedienteEditDialog';
import DocumentsPanel from '../components/DocumentsPanel';
import StructurePanel from '../components/StructurePanel';
import VinculosCard from '../components/VinculosCard';
import ReviewsPanel from '../components/ReviewsPanel';
import ConsentsPanel from '../components/ConsentsPanel';
import ExpedienteActivitySection from '../components/ExpedienteActivity';
import { Check, Circle, ChevronDown, ShieldCheck, Pencil, Trash2 } from 'lucide-react';

interface ActionModal {
  estado: EstadoExpediente;
  title: string;
  confirm: string;
  reasons: string[];
  action: string;
  msg: string;
  tone?: 'default' | 'destructive';
}

const ACTIONS: Record<'escalar' | 'rechazar' | 'aprobar', ActionModal> = {
  escalar: {
    estado: 'Escalado',
    title: 'Escalar a gerencia',
    confirm: 'Escalar',
    reasons: ['PEP detectado', 'Riesgo alto', 'Coincidencia en lista', 'Requiere Debida Diligencia Ampliada'],
    action: 'escaló a revisión de gerencia',
    msg: 'Escalado a Gerencia',
    tone: 'destructive',
  },
  rechazar: {
    estado: 'Rechazado',
    title: 'Rechazar expediente',
    confirm: 'Rechazar',
    reasons: ['Documentación insuficiente', 'Riesgo inaceptable', 'Coincidencia en lista restrictiva', 'Cliente no contactable'],
    action: 'rechazó el expediente',
    msg: 'Expediente rechazado',
    tone: 'destructive',
  },
  aprobar: {
    estado: 'Activo',
    title: 'Aprobar expediente',
    confirm: 'Aprobar',
    reasons: ['Cumple requisitos de Ley 23', 'Riesgo aceptable', 'Documentación completa y vigente'],
    action: 'aprobó el expediente → Activo',
    msg: '✓ Expediente aprobado → Activo',
  },
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/** Per-row reveal stagger (ms) for the validation-run progress sequence. */
const VAL_STAGGER_MS = 400;

type DetailTab = 'documentos' | 'estructura' | 'revisiones' | 'consentimientos';

// Las pestañas dependen de la persona: una persona natural NO tiene Sujetos ni
// Beneficiarios (conceptos solo de organizaciones); una persona jurídica los
// combina en una sola pestaña "Estructura" (sujetos + beneficiarios + grafo).
const TABS_BY_PERSONA: Record<Persona, { key: DetailTab; label: string }[]> = {
  natural: [
    { key: 'documentos', label: 'Documentos' },
    { key: 'revisiones', label: 'Revisiones' },
    { key: 'consentimientos', label: 'Consentimientos' },
  ],
  legal: [
    { key: 'documentos', label: 'Documentos' },
    { key: 'estructura', label: 'Estructura' },
    { key: 'revisiones', label: 'Revisiones' },
    { key: 'consentimientos', label: 'Consentimientos' },
  ],
};

const WEIGHT_TONE: Record<string, Tone> = {
  CRÍTICO: 'red',
  ALTO: 'amber',
  MEDIO: 'blue',
  BAJO: 'green',
};

export default function ExpedienteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: exp, isLoading } = useExpediente(id);
  const { data: me } = useMe();

  const roles = me?.roles ?? [];
  const canEdit = ['compliance_manager', 'intake_officer', 'analyst', 'tenant_admin'].some((r) =>
    roles.includes(r)
  );
  const canDelete = ['compliance_manager', 'intake_officer', 'analyst'].some((r) => roles.includes(r));

  const [valRunning, setValRunning] = useState(false);
  // While a run is animating, how many rows have resolved (null = not animating,
  // so every row shows its settled pill). Drives the top-to-bottom reveal.
  const [revealCount, setRevealCount] = useState<number | null>(null);
  // Sweep the risk ring 0→score once, right after the last row lands.
  const [ringSweep, setRingSweep] = useState(false);
  const reduced = usePrefersReducedMotion();
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ActionModal | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [tab, setTab] = useState<DetailTab>('documentos');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const scrollMemory = useScrollMemory();
  // Restore the scroll position for the newly-active tab. These tabs swap
  // content in place on one scrolling page, and shorter panels let the browser
  // clamp scrollY near the top — restoring keeps the user where they were.
  useLayoutEffect(() => {
    scrollMemory.restore(tab);
  }, [tab]);

  if (isLoading || !exp) {
    return <p className="text-ink-faint">Cargando…</p>;
  }

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['expedientes'] }).then(() =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['expedientes', id] }),
        // Bug fix: validations/estado-transitions/uploads run through this
        // page's own `refresh` (not DocumentsPanel's), so it must ALSO keep
        // Actividad live.
        invalidateExpedienteTimeline(qc, exp.id),
      ])
    );

  async function runVals() {
    if (!exp) return;
    setAdvanced(true);
    setValRunning(true);
    setRingSweep(false);
    // While the API resolves, every row shows the "Ejecutando…" spinner.
    setRevealCount(reduced ? null : 0);
    const rowCount = exp.validations.length;
    try {
      // Screen the client against the sanctions/watchlists (OFAC · ONU · UE · Reino
      // Unido · Canadá). The result drives the "Listas" row and the risk score.
      await api.runValidation('sanctions', {
        subjectType: exp.persona,
        subjectId: exp.id,
        fullName: exp.name,
        ...(exp.docId ? { idNumber: exp.docId } : {}),
      });
      await refresh();
      if (!reduced) {
        // Resolve the pending rows one at a time, top-to-bottom, under the progress bar.
        for (let i = 1; i <= rowCount; i++) {
          await sleep(VAL_STAGGER_MS);
          setRevealCount(i);
        }
        await sleep(250);
      }
      setRingSweep(true);
      toast({ message: 'Validaciones ejecutadas', kind: 'success' });
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'No se pudieron ejecutar las validaciones',
        kind: 'error',
      });
    } finally {
      setValRunning(false);
      setRevealCount(null);
    }
  }

  async function transition(estado: EstadoExpediente, action: string, msg: string) {
    if (!exp) return;
    setBusy(true);
    try {
      await api.setExpedienteEstado(exp.id, estado, action);
      await refresh();
      toast({ message: msg, kind: 'success' });
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'No se pudo cambiar el estado del expediente',
        kind: 'error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function onUploadDoc(file: File, category: string) {
    if (!exp) return;
    try {
      await api.uploadDocument(exp.id, { file, category });
      await refresh();
      setUploadOpen(false);
      toast({ message: `Documento subido — ${file.name}`, kind: 'success' });
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'No se pudo subir el documento',
        kind: 'error',
      });
    }
  }

  async function onDelete() {
    if (!exp) return;
    setDeleting(true);
    try {
      await api.deleteExpediente(exp.id);
      await qc.invalidateQueries({ queryKey: ['expedientes'] });
      await qc.invalidateQueries({ queryKey: ['papelera'] });
      await invalidateExpedienteTimeline(qc, exp.id);
      toast({ message: 'Expediente enviado a la papelera', kind: 'success' });
      navigate('/expedientes');
    } catch (err) {
      toast({
        message: err instanceof Error ? err.message : 'No se pudo eliminar el expediente',
        kind: 'error',
      });
      setDeleting(false);
      setDeleteOpen(false);
    }
  }

  // Persona-dependent tabs. Clamp the active tab to one valid for this persona so
  // it can never get stuck on a now-hidden tab (documentos is always valid).
  const tabs = TABS_BY_PERSONA[exp.persona];
  const activeTab: DetailTab = tabs.some((t) => t.key === tab)
    ? tab
    : (tabs[0]?.key ?? 'documentos');

  const fe = friendlyEstado(exp.estado);
  const allValsPending = exp.validations.every((v) => v.estado === 'pending');
  const aprobado = exp.estado === 'Activo' || exp.estado === 'Aprobado';

  const checklist = [
    { label: 'Datos del cliente', sub: 'Información básica', done: true },
    {
      label: 'Documentos',
      sub: exp.docs.length === 0 ? 'Falta agregar' : `${exp.docs.length} cargado(s)`,
      done: exp.docs.length > 0,
    },
    {
      label: 'Validaciones automáticas',
      sub: allValsPending ? 'Pendientes' : 'Completadas',
      done: !allValsPending,
    },
    { label: 'Aprobación', sub: aprobado ? 'Aprobado' : 'Pendiente', done: aprobado },
  ];
  const doneCount = checklist.filter((c) => c.done).length;
  const enRevision = exp.estado === 'En Revisión' || exp.estado === 'Escalado';

  // One contextual primary action.
  let primary: { label: string; onClick: () => void } | null = null;
  if (exp.docs.length === 0) primary = { label: 'Subir documentos', onClick: () => setUploadOpen(true) };
  else if (allValsPending) primary = { label: 'Ejecutar validaciones', onClick: () => void runVals() };
  else if (!aprobado && exp.estado !== 'Rechazado' && !enRevision)
    primary = {
      label: 'Enviar a revisión',
      onClick: () => void transition('En Revisión', 'envió el expediente a revisión', 'Enviado a revisión'),
    };

  return (
    <>
      {/* section breadcrumb */}
      <div className="mb-4 flex items-center gap-1.5 text-[11px] text-ink-faint">
        <span className="font-semibold uppercase tracking-wider text-primary">Registro</span>
        <span>/</span>
        <button
          onClick={() => navigate('/expedientes')}
          className="hover:text-ink transition-colors"
        >
          Mis clientes
        </button>
      </div>

      {/* header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <span className="grid h-16 w-16 shrink-0 place-items-center rounded-3xl bg-primary/10">
            <PersonaIcon persona={exp.persona} className="h-8 w-8 text-primary-dark" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate font-serif text-2xl font-medium text-ink sm:text-3xl">{exp.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Pill tone={fe.tone}>{fe.label}</Pill>
              {exp.riesgo !== 'sin' && <Pill tone={riesgoTone(exp.riesgo)}>Riesgo {RIESGO_LABEL[exp.riesgo]}</Pill>}
              {exp.pep && <Pill tone="red">PEP</Pill>}
            </div>
          </div>
        </div>
        {(canEdit || canDelete) && (
          <div className="flex shrink-0 items-center gap-2">
            {canEdit && (
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" strokeWidth={2} /> Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="h-4 w-4" strokeWidth={2} /> Eliminar
              </Button>
            )}
          </div>
        )}
      </div>

      {/* ¿Qué falta? checklist */}
      <Card data-tour="detail-checklist" className="mt-6">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-serif text-xl font-medium text-ink">¿Qué falta?</h2>
          <span className="font-mono text-sm text-ink-soft">{doneCount} / {checklist.length}</span>
        </div>
        <ProgressBar value={doneCount / checklist.length} />
        <ul className="mt-5 grid gap-3">
          {checklist.map((c) => (
            <li key={c.label} className="flex items-center gap-3">
              {c.done ? (
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-white">
                  <Check className="h-4 w-4" strokeWidth={3} />
                </span>
              ) : (
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-line-strong">
                  <Circle className="h-6 w-6" strokeWidth={2} />
                </span>
              )}
              <span className="flex-1">
                <span className={c.done ? 'font-semibold text-ink' : 'font-semibold text-ink'}>{c.label}</span>
                <span className="ml-2 text-sm text-ink-faint">{c.sub}</span>
              </span>
            </li>
          ))}
        </ul>
        {primary ? (
          <BigButton className="mt-6" onClick={primary.onClick} disabled={busy || valRunning}>
            {primary.label}
          </BigButton>
        ) : enRevision ? (
          <div className="mt-6 rounded-2xl border border-line bg-canvas px-5 py-4 text-center">
            <p className="font-bold text-ink">En revisión por el equipo de cumplimiento</p>
            <p className="mt-1 text-sm text-ink-soft">Tú ya hiciste tu parte. El analista continúa el proceso.</p>
            <button
              onClick={() => navigate('/alertas')}
              className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-primary-dark"
            >
              Ver en Cumplimiento →
            </button>
          </div>
        ) : (
          <div className="mt-6 flex items-center justify-center gap-2 rounded-2xl bg-primary/8 py-4 text-primary-dark">
            <ShieldCheck className="h-5 w-5" strokeWidth={2} />
            <span className="font-bold">Cliente al día</span>
          </div>
        )}
      </Card>

      {/* reverse ownership links (any persona): entidades donde este sujeto es UBO */}
      <VinculosCard expedienteId={exp.id} />

      {/* expediente detail tabs (Cumplimiento lens) */}
      <div className="mt-6">
        <TabStrip
          tabs={tabs}
          active={activeTab}
          onChange={(k) => {
            scrollMemory.remember(tab);
            setTab(k as DetailTab);
          }}
        />
        {activeTab === 'documentos' && <DocumentsPanel expedienteId={exp.id} />}
        {activeTab === 'estructura' && (
          <StructurePanel
            expedienteId={exp.id}
            companyName={exp.name}
            companyKind={exp.kind}
          />
        )}
        {activeTab === 'revisiones' && (
          <ReviewsPanel expedienteId={exp.id} riesgo={exp.riesgo} review={exp.review} />
        )}
        {activeTab === 'consentimientos' && <ConsentsPanel expedienteId={exp.id} />}
      </div>

      {/* progressive disclosure: compliance detail */}
      <button
        onClick={() => setAdvanced((v) => !v)}
        className="mt-4 flex w-full items-center justify-between rounded-2xl border border-line bg-panel px-5 py-4 text-left shadow-card transition-colors hover:border-ink-faint"
      >
        <span className="font-bold text-ink">Detalle de cumplimiento</span>
        <ChevronDown className={`h-5 w-5 text-ink-soft transition-transform ${advanced ? 'rotate-180' : ''}`} strokeWidth={2} />
      </button>

      {advanced && (
        // `[&>*]:min-w-0` — an implicit-column grid otherwise sizes its track to
        // the widest child's max-content width (chokehold #4): one un-shrinkable
        // line (e.g. a long validation detail) balloons the track and stretches
        // every sibling card with it, clipped invisibly by `main`'s
        // `overflow-x-clip` on mobile. min-w-0 on each card lets it shrink to the
        // available width instead.
        <div className="mt-4 grid gap-4 [&>*]:min-w-0 animate-slide-up">
          {/* validations */}
          <Card>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold text-ink">Validaciones automáticas</h3>
              <Button variant="outline" onClick={() => void runVals()} disabled={valRunning || busy}>
                ⚡ Ejecutar
              </Button>
            </div>
            {/* thin maroon progress bar during the run sequence */}
            {valRunning && (
              <div className="mb-2.5 h-1 w-full overflow-hidden rounded-full bg-line">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                  style={{
                    width:
                      revealCount === null
                        ? '100%'
                        : `${Math.round((revealCount / Math.max(exp.validations.length, 1)) * 100)}%`,
                  }}
                />
              </div>
            )}
            <div className="grid gap-2.5 [&>*]:min-w-0">
              {exp.validations.map((v, i) => {
                // Reveal top-to-bottom: a row is "resolved" once revealCount passes it
                // (revealCount === null means not animating → every row is settled).
                const resolved = revealCount === null ? true : i < revealCount;
                const showSpinner = valRunning && !resolved;
                const VI = valIcon(v.key);
                return (
                  <div key={v.key} className="flex items-center gap-3.5 rounded-xl border border-line bg-canvas px-4 py-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-panel">
                      <VI className="h-4 w-4 text-ink-soft" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-ink">{v.title}</div>
                      <div className="truncate text-[11.5px] text-ink-soft">{v.provider}</div>
                      {v.detail && (
                        <div className="truncate text-[11px] text-ink-faint">{v.detail}</div>
                      )}
                    </div>
                    <span className="ml-auto">
                      {showSpinner ? (
                        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-faint">
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-primary" />
                          Ejecutando…
                        </span>
                      ) : (
                        <Pill
                          tone={valTone(v.estado)}
                          className={valRunning ? 'motion-safe:animate-pop' : ''}
                        >
                          {v.result}
                        </Pill>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* risk */}
          <Card>
            <h3 className="mb-3 text-sm font-bold text-ink">Riesgo</h3>
            <div className="grid gap-6 md:grid-cols-[220px_1fr]">
              <RiskGauge score={exp.score} riesgo={exp.riesgo} cadence={CADENCIA[exp.riesgo]} sweep={ringSweep} />
              <div>
                {exp.factors.length === 0 ? (
                  <p className="text-sm text-ink-faint">Ejecuta las validaciones para calcular el riesgo.</p>
                ) : (
                  <div className="grid gap-2">
                    {exp.factors.map((f, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg border border-line px-3 py-2">
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-ink">{f.name}</div>
                          <div className="font-mono text-[10px] text-ink-faint">{f.source}</div>
                        </div>
                        <Pill tone={WEIGHT_TONE[f.weight] ?? 'slate'} className="font-mono text-[10px]">
                          {f.weight}
                        </Pill>
                      </div>
                    ))}
                  </div>
                )}
                {exp.pep && (
                  <div className="mt-3">
                    <InfoNote tone="amber">
                      ⚠️
                      <div>
                        <b>PEP detectado.</b> Requiere Debida Diligencia Ampliada (EDD) y aprobación de Gerencia.
                      </div>
                    </InfoNote>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* beneficiaries */}
          {exp.ubo && (
            <Card>
              <h3 className="mb-3 text-sm font-bold text-ink">Beneficiarios finales</h3>
              <UboTree name={exp.name} persona={exp.persona} ubo={exp.ubo} />
            </Card>
          )}

          {/* Actividad + Versiones y restauración — audit-backed, both read the
              same GET .../activity feed and share one detail drawer. Replaces
              the dead exp.history stub (always [] in toUiExpediente) and the
              narrower expediente-only VersionHistoryPanel. */}
          <ExpedienteActivitySection expedienteId={exp.id} />

          {/* officer actions */}
          <Card>
            <h3 className="mb-3 text-sm font-bold text-ink">Decisión de cumplimiento</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" disabled={busy} onClick={() => setModal(ACTIONS.escalar)}>
                Escalar
              </Button>
              <Button variant="destructive" disabled={busy} onClick={() => setModal(ACTIONS.rechazar)}>
                Rechazar
              </Button>
              <Button disabled={busy} onClick={() => setModal(ACTIONS.aprobar)}>
                ✓ Aprobar
              </Button>
              <Button variant="ghost" onClick={() => navigate(`/expedientes/${exp.id}/dossier`)}>
                ⬇ Exportar (PDF)
              </Button>
            </div>
          </Card>
        </div>
      )}

      {modal && (
        <ReasonModal
          open
          title={modal.title}
          confirmLabel={modal.confirm}
          reasons={modal.reasons}
          tone={modal.tone}
          onClose={() => setModal(null)}
          onConfirm={(reason) => {
            const mm = modal;
            setModal(null);
            void transition(mm.estado, `${mm.action} (motivo: ${reason})`, mm.msg);
          }}
        />
      )}

      <Dialog open={uploadOpen} title="Agregar documento" onClose={() => setUploadOpen(false)}>
        <DocUploadFlow persona={exp.persona} attached={[]} onUpload={onUploadDoc} />
      </Dialog>

      {editOpen && <ExpedienteEditDialog exp={exp} onClose={() => setEditOpen(false)} />}

      <Dialog open={deleteOpen} title="Eliminar expediente" onClose={() => setDeleteOpen(false)}>
        <p className="text-sm text-ink-soft">
          El expediente de <b className="text-ink">{exp.name}</b> se moverá a la papelera. Podrás
          restaurarlo desde <b className="text-ink">Papelera</b> mientras siga ahí.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={() => void onDelete()} disabled={deleting}>
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
